import "@/App.css";
import React, { Suspense, lazy, useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { ThemeProvider } from "./context/ThemeContext";
import { WebSocketProvider } from "./context/WebSocketContext";
import { OfflineProvider } from "./context/OfflineContext";
import { SyncProvider } from "./context/SyncContext";
import { SearchProvider } from "./contexts/SearchContext";
import { AuthCallback, ProtectedRoute, AdminRoute } from "./components/AuthComponents";
import ErrorBoundary from "./components/ErrorBoundary";
import ModalBackInterceptor from "./components/ModalBackInterceptor";
import { prefetchMap, prefetchAllRoutes } from "./utils/prefetch";

// ─── Lazy-loaded pages ───────────────────────────────────────────────────────
// Module identities come from utils/prefetch so MobileBottomNav etc. can
// trigger the same imports on hover/touch.
const LandingPage         = lazy(() => prefetchMap.landing().then(m => ({ default: m.LandingPage })));
const LoginPage           = lazy(() => prefetchMap.login().then(m => ({ default: m.LoginPage })));
const ResetPasswordPage   = lazy(() => prefetchMap.resetPassword().then(m => ({ default: m.ResetPasswordPage })));
const DashboardPage       = lazy(() => prefetchMap.dashboard().then(m => ({ default: m.DashboardPage })));
const ProductsPage        = lazy(() => prefetchMap.products().then(m => ({ default: m.ProductsPage })));
const ProductFormPage     = lazy(() => prefetchMap.productForm().then(m => ({ default: m.ProductFormPage })));
const ProductDetailPage   = lazy(() => prefetchMap.productDetail().then(m => ({ default: m.ProductDetailPage })));
const ScannerPage         = lazy(() => prefetchMap.scanner().then(m => ({ default: m.ScannerPage })));
const StockMovementPage   = lazy(() => prefetchMap.stockMovement().then(m => ({ default: m.StockMovementPage })));
const LocationsPage       = lazy(() => prefetchMap.locations().then(m => ({ default: m.LocationsPage })));
const ReportsPage         = lazy(() => prefetchMap.reports().then(m => ({ default: m.ReportsPage })));
const UsersPage           = lazy(() => prefetchMap.users().then(m => ({ default: m.UsersPage })));
const NotificationsPage   = lazy(() => prefetchMap.notifications().then(m => ({ default: m.NotificationsPage })));
const SettingsPage        = lazy(() => prefetchMap.settings().then(m => ({ default: m.SettingsPage })));
const SearchPage          = lazy(() => prefetchMap.search().then(m => ({ default: m.SearchPage })));
const UserActivityPage    = lazy(() => prefetchMap.userActivity().then(m => ({ default: m.UserActivityPage })));
const MoveSummaryPage     = lazy(() => prefetchMap.moveSummary().then(m => ({ default: m.MoveSummaryPage })));
const TransactionsPage    = lazy(() => prefetchMap.transactions().then(m => ({ default: m.TransactionsPage })));
const MobileMenuPage      = lazy(() => prefetchMap.mobileMenu().then(m => ({ default: m.MobileMenuPage })));
const UsersManagementPage = lazy(() => prefetchMap.usersManagement().then(m => ({ default: m.UsersManagementPage })));
const ActiveDevicesPage   = lazy(() => prefetchMap.activeDevices().then(m => ({ default: m.ActiveDevicesPage })));
const BackupSettingsPage  = lazy(() => prefetchMap.backupSettings().then(m => ({ default: m.BackupSettingsPage })));
const MaintenancePage     = lazy(() => import('./pages/MaintenancePage').then(m => ({ default: m.MaintenancePage })));
const PickListsPage       = lazy(() => prefetchMap.pickLists().then(m => ({ default: m.PickListsPage })));
const CategoriesPage      = lazy(() => prefetchMap.categories().then(m => ({ default: m.CategoriesPage })));
const AuditPage           = lazy(() => prefetchMap.audit().then(m => ({ default: m.AuditPage })));
const AuditsListPage      = lazy(() => prefetchMap.audits().then(m => ({ default: m.AuditsListPage })));
const AuditCreatePage     = lazy(() => prefetchMap.auditCreate().then(m => ({ default: m.AuditCreatePage })));
const AuditDetailPage     = lazy(() => prefetchMap.auditDetail().then(m => ({ default: m.AuditDetailPage })));
const InvoicingPage       = lazy(() => prefetchMap.invoicing().then(m => ({ default: m.InvoicingPage })));
const InvoiceGeneratorPage = lazy(() => prefetchMap.invoiceGenerator().then(m => ({ default: m.InvoiceGeneratorPage })));
const InvoiceLedgerPage    = lazy(() => import('./pages/InvoiceLedgerPage').then(m => ({ default: m.InvoiceLedgerPage })));
const SupplierStatementPage = lazy(() => import('./pages/SupplierStatementPage'));
const NotificationsSettingsPage = lazy(() => prefetchMap.notificationsSettings().then(m => ({ default: m.NotificationsSettingsPage })));
const SuppliersManagementPage   = lazy(() => prefetchMap.suppliersManagement().then(m => ({ default: m.SuppliersManagementPage })));
const OpeningBalancesPage       = lazy(() => import('./pages/OpeningBalancesPage').then(m => ({ default: m.OpeningBalancesPage })));

// ─── Suspense fallback ───────────────────────────────────────────────────────
// EXPLICITLY null. We never want to flash a skeleton or a bar when the user
// switches tabs — they should stay on the current page until the next chunk
// is ready. With eager prefetch on mount + hover/touch prefetch on the nav
// links, the chunk is virtually always in cache by the time the click
// resolves, so Suspense almost never activates this fallback anyway.
//
// NOTE: returning `null` here means the previous DOM is still unmounted by
// React when Suspense activates — but because activations are extremely rare
// thanks to prefetching, the user perceives "instant + silent" in practice.
const SuspenseFallback = null;

// First-paint auth check is a separate concern: when the AuthContext is still
// reading the JWT from localStorage there is no previous UI to keep, so we
// render a tiny centered spinner. This is NOT the Suspense fallback.
const InitialAuthLoading = () => (
  <div
    className="min-h-screen bg-slate-950 flex items-center justify-center"
    data-testid="initial-auth-loading"
  >
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
  </div>
);

// Eager prefetch — fires once on first mount of <AppRouter />, regardless of
// auth state. The browser handles prioritisation (it runs the active page's
// renders first, then drains the prefetch queue). No requestIdleCallback or
// 8-second wait — chunks start downloading the moment this component mounts.
const RoutePrefetcher = () => {
  useEffect(() => {
    prefetchAllRoutes();
  }, []);
  return null;
};

// ─── Smart top-level routes ──────────────────────────────────────────────────
const SmartLandingRoute = () => {
  const { user, loading } = useAuth();
  if (loading) return <InitialAuthLoading />;
  if (user) return <Navigate to="/locations" replace />;
  return <LandingPage />;
};

const SmartLoginRoute = () => {
  const { user, loading } = useAuth();
  if (loading) return <InitialAuthLoading />;
  if (user) return <Navigate to="/locations" replace />;
  return <LoginPage />;
};

// Reset scroll to top on every route change. Without this, a long page
// scrolled to the bottom keeps that scroll position when the user
// navigates to a new short page on mobile, making it look like the new
// page already scrolled past its header. Use `instant` (not smooth) so
// the new page just appears at the top — no animation jitter.
const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    try {
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    } catch (_) {
      window.scrollTo(0, 0);
    }
  }, [pathname]);
  return null;
};

const AppRouter = () => {
  const location = useLocation();

  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  return (
    <ErrorBoundary key={location.pathname}>
      <ScrollToTop />
      <ModalBackInterceptor />
      <RoutePrefetcher />
      <Suspense fallback={SuspenseFallback}>
        <Routes>
          <Route path="/" element={<SmartLandingRoute />} />
          <Route path="/login" element={<SmartLoginRoute />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />

          <Route path="/dashboard" element={<AdminRoute><DashboardPage /></AdminRoute>} />
          <Route path="/search" element={<ProtectedRoute><SearchPage /></ProtectedRoute>} />
          <Route path="/user-activity" element={<ProtectedRoute><UserActivityPage /></ProtectedRoute>} />
          <Route path="/move-summary" element={<ProtectedRoute><MoveSummaryPage /></ProtectedRoute>} />
          <Route path="/transactions" element={<ProtectedRoute><TransactionsPage /></ProtectedRoute>} />
          <Route path="/mobile-menu" element={<ProtectedRoute><MobileMenuPage /></ProtectedRoute>} />
          <Route path="/products" element={<ProtectedRoute><ProductsPage /></ProtectedRoute>} />
          <Route path="/categories" element={<ProtectedRoute><CategoriesPage /></ProtectedRoute>} />
          <Route path="/products/new" element={<ProtectedRoute><ProductFormPage /></ProtectedRoute>} />
          <Route path="/products/:id" element={<ProtectedRoute><ProductDetailPage /></ProtectedRoute>} />
          <Route path="/products/:id/edit" element={<ProtectedRoute><ProductFormPage /></ProtectedRoute>} />
          <Route path="/products/:id/qr" element={<ProtectedRoute><ProductDetailPage /></ProtectedRoute>} />
          <Route path="/scanner" element={<ProtectedRoute><ScannerPage /></ProtectedRoute>} />
          <Route path="/stock-movement" element={<ProtectedRoute><StockMovementPage /></ProtectedRoute>} />
          <Route path="/pick-lists" element={<ProtectedRoute><PickListsPage /></ProtectedRoute>} />
          <Route path="/locations" element={<ProtectedRoute><LocationsPage /></ProtectedRoute>} />
          <Route path="/audit/:locationId" element={<ProtectedRoute><AuditPage /></ProtectedRoute>} />
          <Route path="/audits" element={<ProtectedRoute><AuditsListPage /></ProtectedRoute>} />
          <Route path="/audits/new" element={<ProtectedRoute><AuditCreatePage /></ProtectedRoute>} />
          <Route path="/audits/:auditId" element={<ProtectedRoute><AuditDetailPage /></ProtectedRoute>} />
          <Route path="/admin/invoicing" element={<ProtectedRoute><InvoicingPage /></ProtectedRoute>} />
          <Route path="/settings/invoice-details" element={<ProtectedRoute><InvoicingPage /></ProtectedRoute>} />
          <Route path="/invoice" element={<ProtectedRoute><InvoiceGeneratorPage /></ProtectedRoute>} />
          <Route path="/invoice/list" element={<ProtectedRoute><InvoiceLedgerPage /></ProtectedRoute>} />
          <Route path="/suppliers/ledger" element={<ProtectedRoute><SupplierStatementPage /></ProtectedRoute>} />
          <Route path="/reports" element={<ProtectedRoute><ReportsPage /></ProtectedRoute>} />
          <Route path="/users" element={<ProtectedRoute><UsersPage /></ProtectedRoute>} />
          <Route path="/admin/users" element={<ProtectedRoute><UsersManagementPage /></ProtectedRoute>} />
          <Route path="/admin/devices" element={<ProtectedRoute><ActiveDevicesPage /></ProtectedRoute>} />
          <Route path="/notifications" element={<ProtectedRoute><NotificationsPage /></ProtectedRoute>} />
          <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
          <Route path="/settings/backup" element={<ProtectedRoute><BackupSettingsPage /></ProtectedRoute>} />
          <Route path="/settings/maintenance" element={<ProtectedRoute><MaintenancePage /></ProtectedRoute>} />
          <Route path="/settings/notifications" element={<ProtectedRoute><NotificationsSettingsPage /></ProtectedRoute>} />
          <Route path="/settings/suppliers" element={<ProtectedRoute><SuppliersManagementPage /></ProtectedRoute>} />
          <Route path="/settings/opening-balances" element={<ProtectedRoute><OpeningBalancesPage /></ProtectedRoute>} />

          <Route path="*" element={<LandingPage />} />
        </Routes>
      </Suspense>
    </ErrorBoundary>
  );
};

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <OfflineProvider>
          <SyncProvider>
            <BrowserRouter>
              <WebSocketProvider>
                <SearchProvider>
                  <AppRouter />
                  <Toaster position="top-right" richColors />
                </SearchProvider>
              </WebSocketProvider>
            </BrowserRouter>
          </SyncProvider>
        </OfflineProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
